import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiHome, FiFolder, FiFile, FiMessageSquare, FiDownload, FiSearch, FiX, FiUploadCloud } from "react-icons/fi";
import { FaFilePdf } from "react-icons/fa";
import { IoMdNotificationsOutline } from "react-icons/io";
import "./Documents.css";

const UserDocumentsPage = () => {

  const [activeTab, setActiveTab] = useState("main");
  const [documents, setDocuments] = useState([]);
  const [chatDocuments, setChatDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [filteredChatDocs, setFilteredChatDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const navigate = useNavigate();

  const sortDocumentsByDate = (docs) => {
    return [...docs].sort((a, b) => {
      const dateA = new Date(a.date || a.timestamp);
      const dateB = new Date(b.date || b.timestamp);
      return dateB - dateA;
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return "-";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  };

  const buildDownloadUrl = (url) => {
    if (!url) return "#";
    let final = url.trim();
    // Clean legacy localhost prefix in stored URL
    final = final.replace(/http:\/\/localhost:5000\//, "/");
    // Clean legacy 'undefined' prefix in stored URL
    final = final.replace(/^undefined\/?/, "/");
    const isAbsolute = /^https?:\/\//i.test(final);
    if (isAbsolute) return final;
    const base = process.env.REACT_APP_API_BASE_URL || "";
    if (final.startsWith("/")) return `${base}${final}`;
    // Fallback: treat as relative uploads path
    return `${base}/${final}`;
  };

  const isNewDocument = (docDate) => {
    const docTime = new Date(docDate).getTime();
    const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    return docTime > weekAgo;
  };

  useEffect(() => {
    const fetchDocuments = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        const sortedDocs = sortDocumentsByDate(data.user.documents || []);
        setDocuments(sortedDocs);
        setFilteredDocs(sortedDocs);
        setFolders(data.user.folders || []);

        const chatResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/documents?userId=${data.user._id}`);
        if (!chatResponse.ok) throw new Error("Failed to fetch chat documents");

        const chatData = await chatResponse.json();
        const sortedChatDocs = sortDocumentsByDate(chatData);
        setChatDocuments(sortedChatDocs);
        setFilteredChatDocs(sortedChatDocs);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
    const interval = setInterval(fetchDocuments, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    if (activeTab === "main") {
      const filtered = documents.filter(doc =>
        (currentFolder ? doc.folderId === currentFolder : !doc.folderId)
      );
      setFilteredDocs(filtered);
    } else {
      const filtered = chatDocuments.filter(doc =>
        true
      );
      setFilteredChatDocs(filtered);
    }
    setPage(1);
  }, [activeTab, documents, chatDocuments, currentFolder]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const openFolder = (folder) => {
    setCurrentFolder(folder._id);
    setFolderPath([...folderPath, folder]);
  };

  const navigateToFolder = (index) => {
    if (index === -1) {
      setCurrentFolder(null);
      setFolderPath([]);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolder(newPath[newPath.length - 1]._id);
    }
  };

  const getCurrentFolderContents = () => {
    if (!currentFolder) return folders.filter(f => !f.parentFolder);
    return folders.filter(f => f.parentFolder === currentFolder);
  };

  const currentDocs = activeTab === "main" ? filteredDocs : filteredChatDocs;
  const count = Math.ceil(currentDocs.length / rowsPerPage);
  const paginatedDocs = currentDocs.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const renderFolders = () => {
    const folderContents = getCurrentFolderContents();
    if (folderContents.length === 0) return null;

    return (
      <div className="folders-container">
        <div className="folders-header">
          <FiFolder className="folder-icon" />
          <h6>Folders</h6>
        </div>
        <div className="folders-grid">
          {folderContents.map((folder) => (
            <div 
              className="folder-card" 
              key={folder._id}
              onClick={() => openFolder(folder)}
            >
              <div className="folder-icon-container">
                <FiFolder className="folder-icon-large" />
              </div>
              <div className="folder-name">{folder.name}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDesktopView = (docs) => (
    <>
      <div className="documents-table">
        <div className="table-header">
          <div className="header-cell document-cell">Document</div>
          <div className="header-cell date-cell">Date</div>
          <div className="header-cell actions-cell">Actions</div>
        </div>
        <div className="table-body">
          {docs.map((document, index) => (
            <div className="table-row" key={index}>
              <div className="table-cell document-cell">
                <div className="document-info">
                  <div className="document-icon-container">
                    <FaFilePdf className="document-icon" />
                    {isNewDocument(document.date || document.timestamp) && (
                      <IoMdNotificationsOutline className="new-badge" />
                    )}
                  </div>
                  <div>
                    <div className="document-name">{document.originalName}</div>
                    <div className="document-size">{formatFileSize(document.size)}</div>
                  </div>
                </div>
              </div>
              <div className="table-cell date-cell">
                {new Date(document.date || document.timestamp).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
              <div className="table-cell actions-cell">
                <a
                  className="download-btn"
                  href={buildDownloadUrl(document.fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiDownload className="download-icon" />
                  Download
                </a>
              </div>
            </div>
          ))}
          {docs.length === 0 && (
            <div className="empty-state">
              <FiFile className="empty-icon" />
              <div className="empty-text">No documents found</div>
            </div>
          )}
        </div>
      </div>
      {docs.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {(page - 1) * rowsPerPage + 1} to {Math.min(page * rowsPerPage, currentDocs.length)} of {currentDocs.length} documents
          </div>
          <div className="pagination-controls">
            <button 
              className="pagination-btn" 
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
            >
              &lt;
            </button>
            {Array.from({ length: count }, (_, i) => i + 1).map(num => (
              <button
                key={num}
                className={`pagination-btn ${page === num ? 'active' : ''}`}
                onClick={() => handlePageChange(num)}
              >
                {num}
              </button>
            ))}
            <button 
              className="pagination-btn" 
              disabled={page === count}
              onClick={() => handlePageChange(page + 1)}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
    </>
  );

  const renderMobileView = (docs) => (
    <>
      <div className="mobile-documents-list">
        {docs.map((document, index) => (
          <div className="mobile-document-card" key={index}>
            <div className="mobile-document-header">
              <div className="mobile-document-icon-container">
                <FaFilePdf className="mobile-document-icon" />
                {isNewDocument(document.date || document.timestamp) && (
                  <IoMdNotificationsOutline className="mobile-new-badge" />
                )}
              </div>
              <div className="mobile-document-name">{document.originalName}</div>
            </div>
            <div className="mobile-document-details">
              <div className="mobile-document-size">{formatFileSize(document.size)}</div>
              <div className="mobile-document-date">
                {new Date(document.date || document.timestamp).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
            <a
              className="mobile-download-btn"
              href={buildDownloadUrl(document.fileUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiDownload className="mobile-download-icon" />
            </a>
          </div>
        ))}
        {docs.length === 0 && (
          <div className="mobile-empty-state">
            <FiFile className="mobile-empty-icon" />
            <div className="mobile-empty-text">No documents found</div>
            <div className="mobile-empty-subtext">
              {searchTerm ? 'Try a different search term' : 'Upload or create a new document to get started'}
            </div>
          </div>
        )}
      </div>
      {docs.length > 0 && (
        <div className="mobile-pagination-container">
          <div className="mobile-pagination-info">
            {Math.min(page * rowsPerPage, currentDocs.length)} of {currentDocs.length}
          </div>
          <div className="mobile-pagination-controls">
            <button 
              className="mobile-pagination-btn" 
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
            >
              &lt;
            </button>
            <span className="mobile-page-number">Page {page} of {count}</span>
            <button 
              className="mobile-pagination-btn" 
              disabled={page === count}
              onClick={() => handlePageChange(page + 1)}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="user-documents-container">
      {/* 1. Instruction Section */}
      <div className="instruction-section">
        <p className="general-instruction">
          View and access all documents: Main Documents for files shared through checklists/steps and Chat documents for files exchanged via Chat.
        </p>
      </div>
      
      {/* 2. Tab Buttons */}
      <div className="documents-header">
        <div className="tabs-container">
          <div 
            className={`tab-item ${activeTab === 'main' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('main');
              setCurrentFolder(null);
              setFolderPath([]);
            }}
          >
            <FiUploadCloud className="tab-icon" />
            <span>Main Documents</span>
          </div>
          <div 
            className={`tab-item ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('chat');
              setCurrentFolder(null);
              setFolderPath([]);
            }}
          >
            <FiMessageSquare className="tab-icon" />
            <span>Chat Documents</span>
          </div>
        </div>
      </div>

      {/* 3. Description Container */}
      <div className="description-container">
        {activeTab === 'main' ? (
          <p className="section-description">
            Documents submitted or shared as part of each step (e.g., Initial Information, Draft Return, Acknowledgement) are saved here for easy reference.
          </p>
        ) : (
          <p className="section-description">
            Documents shared by you or our team via the Chat box are stored here for your quick access.
          </p>
        )}
      </div>

      {/* 4. Folder Container - Only show for Main Documents tab */}
      {activeTab === 'main' && (
        <div className="folder-section">
          {/* Breadcrumbs and Back control */}
          <div className="folder-breadcrumbs">
            <button
              className="back-btn"
              disabled={folderPath.length === 0}
              onClick={() =>
                navigateToFolder(folderPath.length <= 1 ? -1 : folderPath.length - 2)
              }
            >
              Back
            </button>
            <div className="breadcrumbs-trail">
              <span className="crumb home" onClick={() => navigateToFolder(-1)}>
                <FiHome className="crumb-icon" /> Home
              </span>
              {folderPath.map((f, i) => (
                <span key={f._id} className="crumb" onClick={() => navigateToFolder(i)}>
                  <span className="crumb-sep">/</span> {f.name}
                </span>
              ))}
            </div>
          </div>
          {renderFolders()}
        </div>
      )}

      {/* 5. Documents List */}
      <div className="documents-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            {window.innerWidth < 768 ? (
              renderMobileView(paginatedDocs)
            ) : (
              renderDesktopView(paginatedDocs)
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserDocumentsPage;